import { motion } from 'motion/react'
import { ArrowRight, Clock, FileSearch, FileSpreadsheet } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

const features = [
  {
    icon: FileSpreadsheet,
    title: 'No More Excel Chaos',
    description: 'Automated data extraction saves 40+ hours per review',
  },
  {
    icon: FileSearch,
    title: 'Smart Search & Analysis',
    description: 'Find relevant papers in seconds, not hours',
  },
  {
    icon: Clock,
    title: 'Guaranteed Compliance',
    description: 'Built-in MDR compliance checks eliminate errors',
  },
]

export const ProblemSection = () => (
  <section className="relative overflow-hidden bg-[#001871] py-32">
    {/* Enhanced texture overlay */}
    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
    <div className="absolute inset-0 bg-[#001871]/95"></div>

    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="mb-6 text-4xl font-bold text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)]">
            Stop Wasting Time on Manual Literature Reviews
          </h2>
          <p className="mb-8 text-xl text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)]">
            Your team is too valuable to waste time on manual data entry and Excel sheets. See how
            SOTA Navigator transforms your workflow.
          </p>
          <div className="space-y-6">
            {features.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 rounded-xl border border-white/20 bg-white/10 p-6 shadow-lg backdrop-blur-lg"
              >
                <div className="rounded-lg bg-white p-3">
                  <item.icon className="size-6 text-[#001871]" />
                </div>
                <div>
                  <h3 className="mb-1 text-lg font-semibold text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)]">
                    {item.title}
                  </h3>
                  <p className="text-white/90">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <a
            href="https://calendly.com/hatem-rabeh/30-min-seesion-with-dr-hatem-rabeh"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-block"
          >
            <Button
              variant="secondary"
              size="lg"
              className="group gap-2 bg-white text-[#001871] shadow-xl hover:bg-white/90"
            >
              See How It Works
              <ArrowRight className="size-5 text-[#001871] transition-transform group-hover:translate-x-1" />
            </Button>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <Card className="aspect-video overflow-hidden rounded-xl border border-white/20 bg-white/10 shadow-2xl backdrop-blur-lg">
            <CardContent className="flex h-full items-center justify-center">
              <p className="text-xl text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)]">
                Add here a screenshot of the SOTA Navigator dashboard
              </p>
            </CardContent>
          </Card>
          <motion.div
            animate={{
              y: [0, -10, 0],
              rotate: [0, 5, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute -right-10 -top-10 flex size-32 flex-col items-center justify-center rounded-xl bg-white p-4 shadow-xl"
          >
            <Clock className="mb-2 size-8 text-[#001871]" />
            <p className="text-2xl font-bold text-[#001871]">50h+</p>
            <p className="text-sm text-gray-600">Saved/Review</p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
)
